import '@resources/fontes/fontes.css'
import React, { useEffect, useState, useContext, useMemo, ChangeEvent, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BsChevronRight } from 'react-icons/bs'
import Accordion from 'react-bootstrap/Accordion'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import Header from '@components/Header/Header'
import Sidebar from '@components/Sidebar/Sidebar'
import AuthContext from '@contexts/Auth'
import IDonor from '@interfaces/IDonor'
import * as $Order from '@services/Order'
import '@resources/css/order.css'
import '@resources/css/request.css'
import '@resources/css/patient.css'

// Components
import OrderSummary from './components/OrderSummary'
import AccordionReservation from './components/AccordionReservation'
import AccordionConfirmation from './components/AccordionConfirmation'
import AccordionPaymentsAndReceipts from './components/AccordionPaymentsAndReceipts'
import AccordionContract from './components/AccordionContract'
import AccordionTcle from './components/AccordionTcle'
import AccordionSignature from './components/AccordionSignature'
import AccordionClinicRequest from './components/AccordionClinicRequest'
import AccordionShipmentScheduled from './components/AccordionShipmentScheduled'
import AccordionShipped from './components/AccordionShipped'

// Hooks & Utils
import { useOrderProgress, isOrderInSubstatus } from './hooks/useOrderProgress'
import { useOrderData } from './hooks/useOrderData'
import AccordionReceiptsVerification from './components/AccordionReceiptsVerification'
import OrderStatusEnum from '@enums/OrderStatusEnum'
import { FaBuilding, FaStore } from 'react-icons/fa'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { htmlErrorMessage } from '@helpers/Validation'

const Order: React.FC<any> = () => {
  const { user, inProgressOrders, refetchUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const SweetAlert = withReactContent(Swal)

  // State
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [orderFiles, setOrderFiles] = useState<Record<number, { comprovante?: File | null; transportadora?: File | null }>>({})
  const [orderWarnings, setOrderWarnings] = useState<Record<number, string>>({})
  const [samplePaymentMethodTab, setSamplePaymentMethodTab] = useState<string>('Pix')
  const [isRefetchingUser, setIsRefetchingUser] = useState(true)
  const [activeAccordionKey, setActiveAccordionKey] = useState<string | null>(null)
  const hasInitialScrolled = useRef(false)

  // Declaration form state (per order)
  const [orderDeclarationForms, setOrderDeclarationForms] = useState<Record<number, any>>({})
  const [isSubmittingDeclaration, setIsSubmittingDeclaration] = useState(false)

  // TCLE form state (per order)
  const [orderTcleForms, setOrderTcleForms] = useState<Record<number, { matching_choice: string, partner_name: string, partner_email: string }>>({})
  const [isSubmittingTcle, setIsSubmittingTcle] = useState(false)

  // Selected order based on selectedOrderId
  const selectedOrder = useMemo(() => {
    if (!selectedOrderId) return inProgressOrders[0] || null

    return inProgressOrders.find(order => order.id === selectedOrderId) || null
  }, [inProgressOrders, selectedOrderId])

  // Per-order computed values
  const comprovantePagamentoFile = useMemo(() => {
    return selectedOrder ? orderFiles[selectedOrder.id]?.comprovante ?? null : null
  }, [selectedOrder, orderFiles])

  const transportadoraFile = useMemo(() => {
    return selectedOrder ? orderFiles[selectedOrder.id]?.transportadora ?? null : null
  }, [selectedOrder, orderFiles])

  const fileUploadWarning = useMemo(() => {
    return selectedOrder ? orderWarnings[selectedOrder.id] ?? '' : ''
  }, [selectedOrder, orderWarnings])

  const declarationForm = useMemo(() => {
    if (!selectedOrder) return {
      contract_holder: '',
      sample_destination: '',
      third_party_name: '',
      third_party_qualification: '',
      third_party_cpf: '',
      third_party_phone: '',
      third_party_email: '',
      contact_name: '',
      contact_qualification: '',
      contact_phone: '',
      contact_email: '',
      doctor_name: '',
      doctor_crm: ''
    }
    return orderDeclarationForms[selectedOrder.id] || {
      contract_holder: '',
      sample_destination: '',
      third_party_name: '',
      third_party_qualification: '',
      third_party_cpf: '',
      third_party_phone: '',
      third_party_email: '',
      contact_name: '',
      contact_qualification: '',
      contact_phone: '',
      contact_email: '',
      doctor_name: '',
      doctor_crm: ''
    }
  }, [selectedOrder, orderDeclarationForms])

  const tcleForm = useMemo(() => {
    if (!selectedOrder) return {
      matching_choice: '',
      partner_name: user?.patient?.partner_name || '',
      partner_email: user?.patient?.partner_email || ''
    }
    // Local edit wins; otherwise hydrate from server so a submitted order keeps its stored choice on reload
    if (orderTcleForms[selectedOrder.id]) return orderTcleForms[selectedOrder.id]
    return {
      matching_choice: selectedOrder.tcle_matching_choice || '',
      partner_name: user?.patient?.partner_name || '',
      partner_email: user?.patient?.partner_email || ''
    }
  }, [selectedOrder, orderTcleForms, user])

  const isTcleSubmitted = !!selectedOrder?.tcle_accepted_at

  // Sync payment method tab from order data
  useEffect(() => {
    if (!selectedOrder?.payment_method) return
    setSamplePaymentMethodTab(selectedOrder.payment_method === 'pagarme' ? 'Pagar' : 'Pix')
  }, [selectedOrder?.id, selectedOrder?.payment_method])

  // Persist order selection
  const handleSelectOrder = (id: number) => {
    setSelectedOrderId(id)
  }

  // Custom hooks
  const { stepProgress, tcleStepProgress, totalProgress } = useOrderProgress(selectedOrder)
  const { userPhone, clinicAddress, formattedTreatmentDate } = useOrderData(user, selectedOrder)
  const isOrderInSubstatusFlag = selectedOrder ? isOrderInSubstatus(selectedOrder.status as OrderStatusEnum) : false

  // Refetch user data when component mounts to ensure inProgressOrders is up to date
  useEffect(() => {
    refetchUser().finally(() => {
      setIsRefetchingUser(false)
    })
  }, [refetchUser])

  // Redirect if no orders exist after refetching
  useEffect(() => {
    if (!isRefetchingUser && inProgressOrders.length === 0) {
      navigate('/')
    }
  }, [inProgressOrders, navigate, isRefetchingUser])

  // Refetch user data when user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      refetchUser()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [refetchUser])

  // Populate declaration forms with stored data for all orders.
  // Only preserve local edits when the declaration hasn't been submitted yet
  // (sample_destination blank = not submitted). Once submitted the server is authoritative.
  useEffect(() => {
    setOrderDeclarationForms(prev => {
      const newForms: Record<number, any> = { ...prev }
      inProgressOrders.forEach(order => {
        const locallyEdited = !!prev[order.id]
        const localIsEmpty = !prev[order.id]?.sample_destination
        const serverHasData = !!order.sample_destination

        // Always seed from server when: no local entry, or server has data but local is blank
        if (!locallyEdited || (localIsEmpty && serverHasData)) {
          newForms[order.id] = {
            contract_holder: order.contract_holder || '',
            sample_destination: order.sample_destination || '',
            third_party_name: order.third_party_name || '',
            third_party_qualification: order.third_party_qualification || '',
            third_party_cpf: order.third_party_cpf || '',
            third_party_phone: order.third_party_phone || '',
            third_party_email: order.third_party_email || '',
            contact_name: order.contact_name || '',
            contact_qualification: order.contact_qualification || '',
            contact_phone: order.contact_phone || '',
            contact_email: order.contact_email || '',
            doctor_name: order.doctor_name || '',
            doctor_crm: order.doctor_crm || ''
          }
        } else if (!serverHasData) {
          // Declaration not submitted yet: keep doctor fields in sync with the order
          // (set in the Reservation step) so they always come pre-filled, unless the
          // patient already typed a value locally.
          newForms[order.id] = {
            ...prev[order.id],
            doctor_name: prev[order.id]?.doctor_name || order.doctor_name || '',
            doctor_crm: prev[order.id]?.doctor_crm || order.doctor_crm || ''
          }
        }
      })
      return newForms
    })
  }, [inProgressOrders])

  // Auto-scroll and open the running step accordion (only on initial page load)
  useEffect(() => {
    if (!selectedOrder || isRefetchingUser || hasInitialScrolled.current) return

    // TCLE step pending action takes priority
    if (tcleStepProgress === 0) {
      setActiveAccordionKey('tcle')

      if (window.innerWidth >= 768) {
        setTimeout(() => {
          const el = document.querySelector('[data-accordion-key="tcle"]')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 300)
      }

      hasInitialScrolled.current = true
      return
    }

    // Find the step that is currently in progress (50% complete)
    const runningStepIndex = stepProgress.findIndex(progress => progress === 50)

    if (runningStepIndex !== -1) {
      const accordionKey = String(runningStepIndex)
      setActiveAccordionKey(accordionKey)

      // Only scroll on desktop (skip on mobile)
      if (window.innerWidth >= 768) {
        setTimeout(() => {
          const accordionElement = document.querySelector(`[data-accordion-key="${accordionKey}"]`)
          if (accordionElement) {
            accordionElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 300)
      }

      hasInitialScrolled.current = true
    }
  }, [selectedOrder, stepProgress, tcleStepProgress, isRefetchingUser])

  // Check if declaration has been submitted
  const isDeclarationSubmitted = useMemo(() => {
    return !!(selectedOrder?.sample_destination) && selectedOrder?.status !== OrderStatusEnum.SEND_CONTRACT
  }, [selectedOrder])

  // Extract donors from selected order
  const donors = useMemo(() => {
    if (!selectedOrder) return []
    const donorList: IDonor[] = []
    if (selectedOrder.first_donor) donorList.push(selectedOrder.first_donor)
    if (selectedOrder.second_donor) donorList.push(selectedOrder.second_donor)
    if (selectedOrder.third_donor) donorList.push(selectedOrder.third_donor)
    if (selectedOrder.fourth_donor) donorList.push(selectedOrder.fourth_donor)
    if (selectedOrder.fifth_donor) donorList.push(selectedOrder.fifth_donor)
    if (selectedOrder.sixth_donor) donorList.push(selectedOrder.sixth_donor)
    return donorList
  }, [selectedOrder])

  // Photomatching eligibility
  const hasAtLeastThreeDonors = useMemo(() => donors.length >= 3, [donors])

  const hasAtLeastTwoWithSameBank = useMemo(() => {
    const spermBankIds = donors.map(donor => donor.sperm_bank_id).filter(id => id != null)
    const uniqueSpermBankIds = new Set(spermBankIds)
    return spermBankIds.length !== uniqueSpermBankIds.size
  }, [donors])

  const isPhotoMatchAvailable = useMemo(() => {
    return hasAtLeastThreeDonors && hasAtLeastTwoWithSameBank
  }, [hasAtLeastThreeDonors, hasAtLeastTwoWithSameBank])

  const photoMatchData = useMemo(() => {
    if (!isPhotoMatchAvailable) return { donors: [], bankIds: [] }

    const spermBankCounts: Record<string, number> = {}
    donors.forEach(d => {
      const id = d.sperm_bank_id
      if (id) spermBankCounts[id] = (spermBankCounts[id] || 0) + 1
    })
    const eligibleBankIds = Object.keys(spermBankCounts).filter(id => spermBankCounts[id] >= 2)
    const eligibleDonors = donors.filter(d => d.sperm_bank_id && eligibleBankIds.includes(String(d.sperm_bank_id)))
    return { donors: eligibleDonors, bankIds: eligibleBankIds }
  }, [donors, isPhotoMatchAvailable])

  const eligibleBankIds = photoMatchData.bankIds

  // Shipping payment validation
  const shippingPayment = selectedOrder?.shipping_type
  const isTransportPaymentRequired = shippingPayment === 'shipping-company'

  // Handlers
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, fileType: 'comprovantePagamento' | 'transportadora') => {
    const file = event.target.files ? event.target.files[0] : null

    if (!file || !selectedOrder) return

    // Clear any existing warning
    setOrderWarnings(prev => ({ ...prev, [selectedOrder.id]: '' }))

    const currentFiles = orderFiles[selectedOrder.id] || {}

    // Validate that the same file is not being uploaded to both inputs (only if transport payment is required)
    if (isTransportPaymentRequired) {
      if (fileType === 'comprovantePagamento' && currentFiles.transportadora && file.name === currentFiles.transportadora.name && file.size === currentFiles.transportadora.size) {
        setOrderWarnings(prev => ({ ...prev, [selectedOrder.id]: 'Você não pode usar o mesmo arquivo para ambos os comprovantes. Por favor, selecione arquivos diferentes.' }))
        event.target.value = ''
        return
      }

      if (fileType === 'transportadora' && currentFiles.comprovante && file.name === currentFiles.comprovante.name && file.size === currentFiles.comprovante.size) {
        setOrderWarnings(prev => ({ ...prev, [selectedOrder.id]: 'Você não pode usar o mesmo arquivo para ambos os comprovantes. Por favor, selecione arquivos diferentes.' }))
        event.target.value = ''
        return
      }
    }

    if (fileType === 'comprovantePagamento') {
      setOrderFiles(prev => ({
        ...prev,
        [selectedOrder.id]: { ...currentFiles, comprovante: file }
      }))
    } else if (fileType === 'transportadora') {
      setOrderFiles(prev => ({
        ...prev,
        [selectedOrder.id]: { ...currentFiles, transportadora: file }
      }))
    }
  }

  const handleRemoveFile = (fileType: 'comprovantePagamento' | 'transportadora') => {
    if (!selectedOrder) return

    setOrderWarnings(prev => ({ ...prev, [selectedOrder.id]: '' }))
    const currentFiles = orderFiles[selectedOrder.id] || {}

    if (fileType === 'comprovantePagamento') {
      setOrderFiles(prev => ({
        ...prev,
        [selectedOrder.id]: { ...currentFiles, comprovante: null }
      }))
    } else {
      setOrderFiles(prev => ({
        ...prev,
        [selectedOrder.id]: { ...currentFiles, transportadora: null }
      }))
    }
  }

  const handleSubmitPaymentProofs = async (proofType: 'sample' | 'transport' | 'both') => {
    // Validate based on proof type
    if (proofType === 'sample' || proofType === 'both') {
      if (!comprovantePagamentoFile) {
        SweetAlert.fire({
          title: 'Atenção!',
          text: 'Por favor, faça o upload do comprovante de pagamento da amostra de sêmen',
          icon: 'warning',
          confirmButtonText: 'Ok',
        })
        return
      }
    }

    if (proofType === 'transport' || proofType === 'both') {
      if (isTransportPaymentRequired && !transportadoraFile) {
        SweetAlert.fire({
          title: 'Atenção!',
          text: 'Por favor, faça o upload do comprovante de pagamento da transportadora',
          icon: 'warning',
          confirmButtonText: 'Ok',
        })
        return
      }
    }

    if (!selectedOrder?.internal_id) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Pedido não encontrado',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      return
    }

    const proofTypeText = proofType === 'sample'
      ? 'comprovante da amostra de sêmen'
      : proofType === 'transport'
        ? 'comprovante da transportadora'
        : 'comprovantes'

    SweetAlert.fire({
      title: 'Aguarde...',
      text: `Enviando ${proofTypeText}`,
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    try {
      const formData = new FormData()

      // Add files based on proof type
      if ((proofType === 'sample' || proofType === 'both') && comprovantePagamentoFile) {
        formData.append('sample_payment_proof', comprovantePagamentoFile)
      }
      if ((proofType === 'transport' || proofType === 'both') && transportadoraFile) {
        formData.append('transport_payment_proof', transportadoraFile)
      }

      await $Order.submitPaymentProofs(selectedOrder.internal_id, formData)

      await refetchUser()

      // Clear files based on what was submitted
      const currentFiles = orderFiles[selectedOrder.id] || {}
      if (proofType === 'sample' || proofType === 'both') {
        setOrderFiles(prev => ({
          ...prev,
          [selectedOrder.id]: { ...currentFiles, comprovante: null }
        }))
      }
      if (proofType === 'transport' || proofType === 'both') {
        setOrderFiles(prev => ({
          ...prev,
          [selectedOrder.id]: { ...currentFiles, transportadora: null }
        }))
      }

      SweetAlert.fire({
        title: 'Sucesso!',
        text: `${proofTypeText.charAt(0).toUpperCase() + proofTypeText.slice(1)} enviado${proofType === 'both' ? 's' : ''} com sucesso!`,
        icon: 'success',
        confirmButtonText: 'Ok',
      })
    } catch (e: any) {
      console.error('Error submitting payment proofs:', e)

      SweetAlert.fire({
        title: 'Erro!',
        html: htmlErrorMessage(e.response.data, 'Não foi possível realizar o cadastro'),
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    }
  }

  const handleUpdateDoctor = async (doctorName: string, doctorCrm: string) => {
    if (!selectedOrder?.internal_id) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Pedido não encontrado',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      return
    }

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Atualizando dados do médico',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    try {
      await $Order.update(selectedOrder.internal_id, {
        doctor_name: doctorName,
        doctor_crm: doctorCrm,
      })

      await refetchUser()

      SweetAlert.fire({
        title: 'Sucesso!',
        text: 'Dados do médico atualizados com sucesso!',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
    } catch (error: any) {
      console.error('Erro ao atualizar dados do médico:', error)
      SweetAlert.fire({
        title: 'Erro!',
        text: error.response?.data?.message || 'Erro ao atualizar dados do médico. Tente novamente.',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      throw error
    }
  }

  const handleDeclarationFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedOrder) return
    const { name, value } = e.target
    setOrderDeclarationForms(prev => ({
      ...prev,
      [selectedOrder.id]: {
        ...prev[selectedOrder.id],
        [name]: value
      }
    }))
  }

  const handleDeclarationPhoneChange = (field: 'third_party_phone' | 'contact_phone', value: string) => {
    if (!selectedOrder) return
    setOrderDeclarationForms(prev => ({
      ...prev,
      [selectedOrder.id]: {
        ...prev[selectedOrder.id],
        [field]: value
      }
    }))
  }

  const handleDeclarationFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!selectedOrder?.internal_id) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Pedido não encontrado',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      return
    }

    const hasPartner = !!(user?.patient?.partner_name && user?.patient?.partner_document)

    if (hasPartner && !declarationForm.contract_holder) {
      SweetAlert.fire({
        title: 'Atenção!',
        text: 'Selecione o titular do contrato.',
        icon: 'warning',
        confirmButtonText: 'Ok',
      })
      return
    }

    setIsSubmittingDeclaration(true)

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Enviando declaração',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    try {
      const declarationPayload = {
        ...declarationForm,
        contract_holder: hasPartner ? declarationForm.contract_holder : 'patient',
      }

      await $Order.fillDeclarationForm(selectedOrder.internal_id, declarationPayload)

      // Wait 3 seconds for ClickSign webhook to store contract data
      await new Promise(resolve => setTimeout(resolve, 3000))

      await refetchUser()

      SweetAlert.fire({
        title: 'Sucesso!',
        text: 'Declaração preenchida com sucesso!',
        icon: 'success',
        confirmButtonText: 'Ok',
      }).then(() => {
        setActiveAccordionKey('6')
      })
    } catch (error: any) {
      console.error('Erro ao enviar declaração:', error)
      SweetAlert.fire({
        title: 'Erro!',
        text: error.response?.data?.message || 'Erro ao enviar declaração. Tente novamente.',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    } finally {
      setIsSubmittingDeclaration(false)
    }
  }

  const handleTcleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedOrder) return
    const { name, type, value, checked } = e.target
    setOrderTcleForms(prev => ({
      ...prev,
      [selectedOrder.id]: {
        ...prev[selectedOrder.id],
        [name]: type === 'checkbox' ? checked : value
      }
    }))
  }

  const handleTcleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!selectedOrder?.internal_id) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Pedido não encontrado',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      return
    }

    if (!tcleForm.matching_choice) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Selecione uma opção de matching.',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
      return
    }

    setIsSubmittingTcle(true)

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Enviando TCLE',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    try {
      await $Order.fillTcleForm(selectedOrder.internal_id, {
        matching_choice: tcleForm.matching_choice as 'matching' | 'no_matching',
        partner_name: tcleForm.partner_name || undefined,
        partner_email: tcleForm.partner_email || undefined,
      })

      await refetchUser()

      SweetAlert.fire({
        title: 'Sucesso!',
        text: 'TCLE enviado com sucesso!',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
    } catch (error: any) {
      console.error('Erro ao enviar TCLE:', error)
      SweetAlert.fire({
        title: 'Erro!',
        text: error.response?.data?.message || 'Erro ao enviar TCLE. Tente novamente.',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    } finally {
      setIsSubmittingTcle(false)
    }
  }

  return (
    <div className="patient-summary d-lg-flex justify-content-lg-center">
      <Sidebar />
      <main className="with-fixed-nav">
        <Header showSidebarOpenButton />

        {/* Breadcrumb */}
        <div className="breadcrumb-section mt-4 mb-3">
          <Link to="/" className="breadcrumb-link">Catálogo</Link>
          <BsChevronRight className="breadcrumb-separator" size={12} />
          <span className="breadcrumb-current">Pedidos</span>
        </div>

        {/* Order Selection Cards */}
        {inProgressOrders.length > 1 && (
          <div className="order-selection mb-4">
            <h5 className="mb-3">Meus Pedidos</h5>
            <div className="d-flex gap-15 flex-wrap order-selection__cards">
              {inProgressOrders.map((order) => {
                const isSelected = selectedOrderId ? order.id === selectedOrderId : order.id === inProgressOrders[0].id
                const orderDate = new Date(order.created_at).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })

                // Status labels
                const statusLabels: Record<string, string> = {
                  [OrderStatusEnum.RESERVATION_REQUEST]: 'Solicitação de Reserva',
                  [OrderStatusEnum.PHOTOMATCH_IN_ANALYSIS]: 'Photomatch em Análise',
                  [OrderStatusEnum.WAITING_PAYMENT]: 'Aguardando Pagamento',
                  [OrderStatusEnum.WAITING_PAYMENT_TRANSPORT]: 'Aguardando Pagamento',
                  [OrderStatusEnum.WAITING_PAYMENT_VERIFICATION]: 'Verificando Pagamento',
                  [OrderStatusEnum.SEND_CONTRACT]: 'Envio de Contrato',
                  [OrderStatusEnum.WAITING_SIGNATURE]: 'Aguardando Assinatura',
                  [OrderStatusEnum.CONTRACT_SIGNED]: 'Contrato Assinado',
                  [OrderStatusEnum.REQUEST_FOR_FORM]: 'Solicitação de SEA',
                  [OrderStatusEnum.WAITING_SEND_REQUEST]: 'Aguardando Envio',
                  [OrderStatusEnum.SEND_SCHEDULED]: 'Envio Agendado',
                  [OrderStatusEnum.SENDED]: 'Enviado',
                  [OrderStatusEnum.DELIVERED]: 'Entregue',

                  [OrderStatusEnum.PENDING]: 'Pendente',
                  [OrderStatusEnum.GENETICS]: 'Genética',
                  [OrderStatusEnum.MAINTENANCE_FEE]: 'Taxa de Manutenção',
                  [OrderStatusEnum.RESERVATION_FEE]: 'Taxa de Reserva',
                  [OrderStatusEnum.CUSTOM_IMPORT]: 'Importação Personalizada',
                  [OrderStatusEnum.AFTER_SALES]: 'Pós-venda',

                  [OrderStatusEnum.CANCELED]: 'Cancelado'
                }

                const statusLabel = statusLabels[order.status] || 'Em Processamento'
                const isCanceled = order.status === OrderStatusEnum.CANCELED

                return (
                  <div
                    key={order.id}
                    onClick={() => handleSelectOrder(order.id)}
                    style={{
                      cursor: 'pointer',
                      padding: '20px 24px',
                      borderRadius: '12px',
                      border: isCanceled
                        ? '2px solid #d0d0d0'
                        : isSelected ? '2px solid #4d61aa' : '2px solid #e0e0e0',
                      backgroundColor: isCanceled
                        ? '#f5f5f5'
                        : isSelected ? '#f3f5fb' : '#ffffff',

                      transition: 'all 0.3s ease',
                      boxShadow: isCanceled
                        ? '0 2px 6px rgba(0, 0, 0, 0.06)'
                        : isSelected
                          ? '0 4px 12px rgba(77, 97, 170, 0.15)'
                          : '0 2px 8px rgba(0, 0, 0, 0.08)',
                      transform: isSelected ? 'translateY(-2px)' : 'none',
                      opacity: isCanceled ? 0.7 : 1,
                      filter: isCanceled ? 'grayscale(0.5)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !isCanceled) {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)'
                        e.currentTarget.style.borderColor = '#b0b0b0'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !isCanceled) {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'
                        e.currentTarget.style.borderColor = '#e0e0e0'
                      } else if (isCanceled) {
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.06)'
                        e.currentTarget.style.borderColor = '#d0d0d0'
                      }
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: isSelected ? '#4d61aa' : '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                      >
                        Pedido
                      </span>
                      {isSelected && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#4d61aa',
                          boxShadow: '0 0 0 3px rgba(77, 97, 170, 0.2)'
                        }}
                        />
                      )}
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: '#2c3e50',
                      marginBottom: '12px'
                    }}
                    >
                      #{order.internal_id}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px'
                    }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {orderDate}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: '4px'
                    }}
                    >
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: isCanceled ? '#6c757d' : '#4d61aa',
                        backgroundColor: isCanceled ? 'rgba(108, 117, 125, 0.1)' : 'rgba(77, 97, 170, 0.1)',
                        padding: '6px 10px',
                        borderRadius: '6px'
                      }}
                      >
                        {statusLabel}
                      </div>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-source-${order.id}`}>
                          {order.source === 'erp' ? 'Pedido criado pelo Pro-Seed' : 'Pedido criado através do Portal'}
                        </Tooltip>}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: isSelected ? '#4d61aa' : '#6c757d',
                          transition: 'color 0.3s ease',
                          marginRight: ' -3px'
                        }}
                        >
                          {order.source === 'erp' ? <FaBuilding size={16} /> : <FaStore size={16} />}
                        </div>
                      </OverlayTrigger>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Order Summary */}
        <OrderSummary
          inProgressOrder={selectedOrder}
          totalProgress={totalProgress}
        />

        {/* Accordions */}
        {selectedOrder?.status !== OrderStatusEnum.CANCELED && (
          <section className="accordion box-card">
            <div className="title">
              <h3>Status da Sua Solicitação</h3>
            </div>

            <Accordion activeKey={activeAccordionKey ?? undefined} onSelect={(key) => setActiveAccordionKey(key as string | null)}>
              <div data-accordion-key="0">
                <AccordionReservation
                  stepProgress={stepProgress[0]}
                  inProgressOrder={selectedOrder}
                  clinicCity={clinicAddress?.city || ''}
                  clinicState={clinicAddress?.state || ''}
                  formattedTreatmentDate={formattedTreatmentDate}
                  user={user}
                  userPhone={userPhone}
                  donors={donors}
                  isLoadingDonors={false}
                  eligibleBankIds={eligibleBankIds}
                  onUpdateDoctor={handleUpdateDoctor}
                />
              </div>

              {selectedOrder?.requires_tcle && (
                <div data-accordion-key="tcle">
                  <AccordionTcle
                    stepProgress={tcleStepProgress ?? 0}
                    inProgressOrder={selectedOrder}
                    user={user}
                    form={tcleForm}
                    isSubmitting={isSubmittingTcle}
                    isSubmitted={isTcleSubmitted}
                    onFormChange={handleTcleFormChange}
                    onFormSubmit={handleTcleFormSubmit}
                  />
                </div>
              )}

              <div data-accordion-key="1">
                <AccordionConfirmation
                  stepProgress={stepProgress[1]}
                  inProgressOrder={selectedOrder}
                />
              </div>

              <div data-accordion-key="2">
                <AccordionPaymentsAndReceipts
                  sampleStepProgress={stepProgress[2]}
                  transportStepProgress={stepProgress[3]}
                  samplePaymentMethodTab={samplePaymentMethodTab}
                  setSamplePaymentMethodTab={setSamplePaymentMethodTab}
                  user={user}
                  inProgressOrder={selectedOrder}
                  comprovantePagamentoFile={comprovantePagamentoFile}
                  transportadoraFile={transportadoraFile}
                  fileUploadWarning={fileUploadWarning}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  onSubmitPaymentProofs={handleSubmitPaymentProofs}
                  isOrderInSubstatus={isOrderInSubstatusFlag}
                />
              </div>

              <div data-accordion-key="4">
                <AccordionReceiptsVerification
                  stepProgress={stepProgress[4]}
                  inProgressOrder={selectedOrder}
                />
              </div>

              <div data-accordion-key="5">
                <AccordionContract
                  stepProgress={stepProgress[5]}
                  orderId={selectedOrder?.id ?? null}
                  user={user}
                  declarationForm={declarationForm}
                  isSubmittingDeclaration={isSubmittingDeclaration}
                  isDeclarationSubmitted={isDeclarationSubmitted}
                  onFormChange={handleDeclarationFormChange}
                  onFormSubmit={handleDeclarationFormSubmit}
                  onPhoneChange={handleDeclarationPhoneChange}
                  isOrderInSubstatus={isOrderInSubstatusFlag}
                  orderStatus={selectedOrder?.status as OrderStatusEnum ?? null}
                />
              </div>

              <div data-accordion-key="6">
                <AccordionSignature
                  stepProgress={stepProgress[6]}
                  inProgressOrder={selectedOrder}
                />
              </div>

              <div data-accordion-key="7">
                <AccordionClinicRequest
                  stepProgress={stepProgress[7]}
                  inProgressOrder={selectedOrder}
                />
              </div>

              <div data-accordion-key="8">
                <AccordionShipmentScheduled
                  stepProgress={stepProgress[8]}
                  inProgressOrder={selectedOrder}
                />
              </div>

              <div data-accordion-key="9">
                <AccordionShipped stepProgress={stepProgress[9]} />
              </div>
            </Accordion>
          </section>
        )}
      </main>
    </div>
  )
}

export default Order
